import React, { useEffect, useState } from "react";
import Layout from "../../../layout/Layout";
import Lottie from "lottie-react";


import EmployeeCardImage1 from '../../../assets/hrm/EmployeeId1.jpg';
import EmployeeCardImage2 from '../../../assets/hrm/EmployeeId2.jpg';

import EmployeeManagementImage1 from '../../../assets/hrm/ManageEmployees1.jpg';
import EmployeeManagementImage2 from '../../../assets/hrm/ManageEmployees2.jpg';

import LeaveBalanceImage from '../../../assets/hrm/LeaveBalance.jpg';
import LeaveCalendarImage from '../../../assets/hrm/LeaveCalendar.jpg';

import attendanceAnimation from '../../../animations/hrm/attendance.json';

import FinancialMetricsImage from '../../../assets/hrm/FinancialMetrics.jpg';
import TopPerformersImage from '../../../assets/hrm/TopPerformers.jpg';

const WorkflowStep = ({ step, active }) => (
    <div
      className={`relative transition-transform duration-500 ease-in-out opacity-50 scale-90 border rounded-lg p-4 mx-2 max-w-sm bg-gray-700 shadow-lg text-white ${
        active ? "opacity-100 scale-105 border-cyan-400 shadow-xl bg-cyan-900" : "border-gray-500"
      }`}
    >
      <h3 className="text-md font-bold mb-1 text-white">{step.title}</h3>
      <p className="text-sm text-gray-300">{step.description}</p>
    </div>
  );
  
  const CircularArrow = ({ active }) => (
    <div className="relative flex justify-center items-center h-12 w-12 mx-2">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke={active ? "#00FFFF" : "#4B5563"}
        strokeWidth="2"
        className={`transition-transform duration-500 ${
          active ? "scale-125 rotate-180" : "scale-100 rotate-0"
        } h-full w-full`}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 2c5.523 0 10 4.477 10 10s-4.477 10-10 10-10-4.477-10-10 4.477-10 10-10zm0 0v4m0 6h4"
        />
      </svg>
    </div>
  );
  
  const Workflow = () => {
    const steps = [
      {
        title: "Request Overtime",
        description: "Submit an overtime request for approval."
      },
      {
        title: "Approval",
        description: "Manager reviews and approves the request."
      },

      {
        title: "Payroll Adjustment",
        description: "Overtime is added to payroll adjustments."
      }
    ];
  
    const [activeStep, setActiveStep] = useState(0);
  
    useEffect(() => {
      const interval = setInterval(() => {
        setActiveStep((prevStep) => (prevStep + 1) % steps.length);
      }, 2000); // Change step every 2 seconds
  
      return () => clearInterval(interval);
    }, []);
  
    return (
      <div className="w-full flex flex-col items-center">
          {steps.map((step, index) => (
            <React.Fragment key={index}>
              <WorkflowStep step={step} active={index === activeStep} />
              {index < steps.length - 1 && (
                <CircularArrow active={index === activeStep} />
              )}
            </React.Fragment>
          ))}
      </div>
    );
  };


function HRMManagement() {


  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative bg-[#0A0F1F] text-white py-10 mt-28 overflow-hidden">
        <div className="container mx-auto px-6 py-10 lg:flex lg:items-center lg:justify-between">
          {/* Left Section */}
          <div className="lg:w-1/1 text-center lg:text-left">
            <h1 className="font-larger mb-10">HUMAN RESOURCE MANAGEMENT</h1>
            <p className="mt-6 text-gray-300 max-w-md">
              <b>Streamline your HR processes, manage employees, payroll, and attendance effortlessly.</b>
            </p>
            <div className="mt-8 flex justify-center lg:justify-start gap-4">
              <button className="px-8 py-3 bg-yellow-400 text-gray-900 font-semibold rounded-md shadow-md hover:bg-yellow-300 transition">
                Get Started
              </button>
            </div>
          </div>

          {/* Right Section */}
          <div className="lg:w-2/3 relative">
          </div>
        </div>
      </section>


      {/* Employee Management Portal */}
      <section className="border border-dashed border-green-900 py-16 mx-2 text-white bg">
        <div className="container mx-auto mb-12 text-center">
            <h1 className="text-4xl font-bold text-green-400">Employee Management</h1>
            <p className="mt-4 text-lg text-gray-400">
                Add, edit, and manage employee details from one centralized platform.
            </p>
        </div>

        <div className="container mx-auto grid grid-cols-1 md:grid-cols-2 gap-12">
        
            {/* Generalized Employee Management */}
            <div className="p-8 rounded-lg shadow-md flex items-center justify-center">
                <div>
                    <h2 className="text-2xl font-semibold text-green-400">Seamless Employee Operations</h2>
                    <p className="mt-4 text-gray-300">
                        Effortlessly add, update, activate, deactivate, and search employees—all from a unified dashboard designed for efficiency and clarity.
                    </p>
                </div>
            </div>

            {/* Employee Management Images */}
            <div className="relative p-8 rounded-lg shadow-md flex justify-center items-center">
                {/* First Image */}
                <img 
                    src={EmployeeManagementImage1} 
                    alt="Employee Management 1" 
                    className="w-[60%] h-auto rounded-lg shadow-lg transform rotate-6 transition duration-500 hover:scale-105 hover:rotate-0 z-10"
                />
                {/* Second Image */}
                <img 
                    src={EmployeeManagementImage2} 
                    alt="Employee Management 2" 
                    className="w-[60%] h-auto rounded-lg shadow-lg transform -rotate-6 -translate-x-16 translate-y-8 transition duration-500 hover:scale-105 hover:rotate-0 z-0"
                />
            </div>



            {/* Employee Card Image */}
            <div className="relative p-8 rounded-lg shadow-md flex justify-center items-center">
                {/* Second Image - Positioned Higher */}
                <img 
                    src={EmployeeCardImage2} 
                    alt="Employee Management 2" 
                    className="w-[60%] h-auto rounded-lg shadow-lg transform -rotate-12 -translate-x-24 -translate-y-10 transition duration-500 hover:scale-105 hover:rotate-0 z-0"
                />

                {/* First Image - Positioned Over Second Image */}
                <img 
                    src={EmployeeCardImage1} 
                    alt="Employee Management 1" 
                    className="w-[60%] h-auto rounded-lg shadow-xl transform rotate-12 translate-y-4 translate-x-24 transition duration-500 hover:scale-105 hover:rotate-0 z-10 absolute"
                />
            </div>


            {/* Employee ID Cards Section */}
            <div className="p-8 rounded-lg shadow-md flex items-center justify-center">
                <div>
                    <h2 className="text-2xl font-semibold text-green-400">Professional Employee ID Cards</h2>
                    <p className="mt-4 text-gray-300">
                        Generate sleek, printable ID cards tailored to each employee, ensuring professionalism in every representation.
                    </p>
                </div>
            </div>
        </div>
      </section>


      {/* Time and Attendance Management */}
    <section className="border border-dashed border-green-900 py-16 mt-20 mx-2 text-white bg">
        <div className="container mx-auto mb-12 text-center">
            <h1 className="text-4xl font-bold text-green-400">Time and Attendance Management</h1>
            <p className="mt-4 text-lg text-gray-400">
                Simplify workforce tracking with automated attendance logs, leave approvals, and overtime management—all in one place.
            </p>
        </div>

        <div className="container mx-auto grid grid-cols-1 md:grid-cols-2 gap-12">
            
            {/* Attendance Management */}
            <div className="text-left p-8 mt-10 rounded-lg shadow-md flex items-center justify-center">
                <div>
                    <h2 className="text-2xl font-semibold text-green-400">Attendance Tracking</h2>
                    <p className="mt-4 text-gray-300">
                        Maintain accurate attendance records with editable sheets. View and approve daily logs, monitor absent employees, and analyze monthly attendance rates in real-time.
                    </p>
                    <ul className="mt-4 text-sm text-gray-400 list-disc list-inside">
                        <li>Editable Attendance Sheets for Daily and Monthly Records</li>
                        <li>Real-Time Overall Attendance Rate Dashboard</li>
                        <li>Highlight Absent Employees for Immediate Action</li>
                    </ul>
                </div>
            </div>

            {/* Attendance Management Images */}
            <div className="relative p-8 mt-10 rounded-lg shadow-md flex justify-center items-center">
                
                <Lottie animationData={attendanceAnimation} loop={true} />
            </div>

            {/* Leave Management Images*/}
            <div className="relative p-8 mt-20 rounded-lg shadow-md flex justify-center items-center">
                {/* First Image - Positioned Over Second Image */}
                <img 
                    src={LeaveCalendarImage} 
                    alt="Employee Management 1" 
                    className="w-[90%] h-auto rounded-lg shadow-xl transform rotate-0 -translate-y-10 -translate-x-6 transition duration-500 hover:scale-105 hover:rotate-0 z-0 absolute"
                />

                {/* Second Image - Positioned Higher */}
                <img 
                    src={LeaveBalanceImage} 
                    alt="Employee Management 2" 
                    className="w-[60%] h-auto rounded-lg shadow-lg transform -rotate-12 translate-x-40 translate-y-16 transition duration-500 hover:scale-105 hover:rotate-0 z-10"
                />

                
            </div>

            

            {/* Leave Management */}
            <div className="text-left mt-10 p-8 rounded-lg shadow-md flex items-center justify-center">
                <div>
                    <h2 className="text-2xl font-semibold text-green-400">Leave Management</h2>
                    <p className="mt-4 text-gray-300">
                        Handle leave requests effortlessly with automated workflows. Review balances, approve or reject requests, and maintain transparency in leave allocation.
                    </p>
                    <ul className="mt-4 text-sm text-gray-400 list-disc list-inside">
                        <li>Automated Leave Requests and Approval Workflow</li>
                        <li>Track Leave Balances and History</li>
                        <li>Integrated Calendar View for Scheduling</li>
                        <li>Leave Summaries and Reports</li>
                    </ul>
                </div>
            </div>


            {/* Overtime Management */}
            <div className="text-left p-8 mt-10  rounded-lg shadow-md flex items-center justify-center">
                <div>
                    <h2 className="text-2xl font-semibold text-green-400">Overtime Management</h2>
                    <p className="mt-4 text-gray-300">
                        Record and approve overtime hours seamlessly. Integrate with payroll systems to ensure accurate compensation for extra work.
                    </p>
                    <ul className="mt-4 text-sm text-gray-400 list-disc list-inside">
                        <li>Submit and Approve Overtime Requests</li>
                        <li>Track Overtime Hours and Costs</li>
                        <li>Real-Time Overtime Analysis Reports</li>
                        <li>Payroll Integration for Automatic Calculations</li>
                    </ul>
                </div>
            </div>

            {/* Overtime Workflow */}
            <div className="p-8 mt-10 rounded-lg shadow-md flex flex-col gap-8 items-center ">
                    <Workflow />
            </div>

        </div>
    </section>

    {/* Performance Management Portal */}
    <section className="border border-dashed border-green-900 py-16 mx-2 mt-20 text-white bg">
    <div className="container mx-auto mb-12 text-center">
        <h1 className="text-4xl font-bold text-green-400">Performance Management</h1>
        <p className="mt-4 text-lg text-gray-400">
        Monitor, evaluate, and enhance employee performance through KPIs, goals, reviews, and payroll adjustments.
        </p>
    </div>

    <div className="container mx-auto grid grid-cols-1 md:grid-cols-2 gap-12">
        {/* KPI and Goal Management */}
        <div className="text-left p-8 rounded-lg shadow-md flex items-center justify-center">
        <div>
            <h2 className="text-2xl font-semibold text-green-400">Define KPIs and Goals</h2>
            <p className="mt-4 text-gray-300">
            Set key performance indicators and goals, and align them with business objectives, team efforts, and individual tasks.
            </p>
        </div>
        </div>

        {/* Performance Association */}
        <div className="text-left p-8 rounded-lg shadow-md flex items-center justify-center">
        <div>
            <h2 className="text-2xl font-semibold text-green-400">Associate Goals with Leads & Sales</h2>
            <p className="mt-4 text-gray-300">
            Link individual and team goals to sales, leads, and other measurable metrics for accurate performance tracking.
            </p>
        </div>
        </div>

        {/* Performance Reviews */}
        <div className="text-left p-8 rounded-lg shadow-md flex items-center justify-center">
        <div>
            <h2 className="text-xl font-semibold text-green-400">Comprehensive Performance Reviews</h2>
            <p className="mt-4 text-gray-300">
            Conduct structured reviews, gather feedback, and generate performance reports to assess achievements and areas for growth.
            </p>
        </div>
        </div>

        {/* Payroll Adjustments */}
        <div className="text-left p-8 rounded-lg shadow-md flex items-center justify-center">
        <div>
            <h2 className="text-2xl font-semibold text-green-400">Impactful Payroll Adjustments</h2>
            <p className="mt-4 text-gray-300">
            Automatically adjust payroll based on performance evaluations, reflecting bonuses, appraisals, and other incentives.
            </p>
        </div>
        </div>
        
        
    </div>
    </section>

    {/* Payroll Adjustment Management Section */}
    <section className="border border-dashed border-green-900 py-16 mx-2 mt-20 text-white bg">
    <div className="container mx-auto mb-12 text-center">
        <h1 className="text-4xl font-bold text-green-400">Payroll Adjustment</h1>
        <p className="mt-4 text-lg text-gray-400">
        Streamline payroll adjustments and approvals while tracking financial and performance impacts across departments.
        </p>
    </div>

    <div className="container mx-auto grid grid-cols-1 md:grid-cols-2 gap-12">
        {/* Payroll Adjustment Slips */}
        <div className="text-left p-8 rounded-lg shadow-md flex items-center justify-center">
        <div>
            <h2 className="text-2xl font-semibold text-green-400">Create Payroll Adjustment Slips</h2>
            <p className="mt-4 text-gray-300">
            Easily generate adjustment slips for bonuses, deductions, or overtime, tailored to individual employees.
            </p>
        </div>
        </div>

        {/* Approve Adjustments */}
        <div className="text-left p-8 rounded-lg shadow-md flex items-center justify-center">
        <div>
            <h2 className="text-xl font-semibold text-green-400">Approve Adjustments for payroll Processing</h2>
            <ul className="mt-4 text-sm text-gray-400 list-disc list-inside">
                <li>Consolidate all adjustments for each employee and integrate them seamlessly into their payroll entries.</li>
                <li>Review and approve payroll adjustments to ensure accuracy and compliance before processing.</li>
            </ul>
            
        </div>
        </div>

        {/* Financial and Performance Metrics */}
        <div className="text-left p-8 rounded-lg shadow-md flex items-center justify-center">
        <div>
            <h2 className="text-xl font-semibold text-green-400">Track Financial and Performance Metrics</h2>
            <p className="mt-4 text-gray-300">
            Analyze the financial impact of payroll adjustments by department and review employee performance metrics.
            </p>
        </div>
        </div>
        
        {/* Visual Section with Metrics Charts */}
        <div className="relative p-8 rounded-lg shadow-md flex justify-center items-center">
        {/* Example Metric Chart Image */}
        <img 
            src={FinancialMetricsImage} 
            alt="Financial Metrics Chart" 
            className="w-[100%] h-auto rounded-lg shadow-lg transform rotate-6 transition duration-500 hover:scale-105 hover:rotate-0 z-0"
        />
        {/* Example Adjustment Slip Image */}
        <img 
            src={TopPerformersImage} 
            alt="Top Performers" 
            className="w-[50%] h-auto rounded-lg shadow-lg transform -rotate-6 -translate-x-16 -translate-y-10 transition duration-500 hover:scale-105 hover:rotate-0 z-10"
        />
        </div>
    </div>
    </section>



    </Layout>
  );
}

export default HRMManagement;
